import { setUserProperties } from 'firebase/analytics';
import Cookies from 'js-cookie';
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { hubxAnalytic } from '@/app/providers/analytics';
import { analytics } from '@/app/providers/firebase';

interface ExperimentContextType {
  experiment: ExperimentType;
  anonymousId: string | undefined;
  experimentId: string | undefined;
  variationId: string | undefined;
  setExperimentDetails: (experimentKey: string, variationKey: string) => void;
}

type ExperimentType = 'current-flow' | 'landing-flow';

const ExperimentContext = createContext<ExperimentContextType | null>(null);

export function ExperimentProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const [experiment, setExperiment] = useState<ExperimentType>();
  const [anonymousId, setAnonymousId] = useState<string>();
  const [experimentId, setExperimentId] = useState<string>();
  const [variationId, setVariationId] = useState<string>();

  useEffect(() => {
    setAnonymousId(hubxAnalytic.getUUID());

    const gbExperiment = Cookies.get('gb_experiment');
    if (gbExperiment !== undefined) {
      setExperimentId(gbExperiment);
    }

    const gbVariation = Cookies.get('gb_variation');
    if (gbVariation !== undefined) {
      setVariationId(gbVariation);
    }

    setUserProperties(analytics, {
      experiment: gbExperiment,
      variation: gbVariation,
    });
  }, []);

  const setExperimentDetails = (
    experimentKey: string,
    variationKey: string,
  ): void => {
    setExperimentId(experimentKey);
    setVariationId(variationKey);

    Cookies.set('gb_experiment', experimentKey, {
      expires: 7,
    });
    Cookies.set('gb_variation', variationKey, {
      expires: 7,
    });
  };

  if (experiment === undefined) {
    const experimentCookie = Cookies.get('experiment');
    if (experimentCookie === 'landing-flow') {
      setExperiment('landing-flow');
    } else {
      setExperiment('current-flow');
    }
    return null;
  }

  return (
    <ExperimentContext.Provider
      value={{
        experiment,
        anonymousId,
        experimentId,
        variationId,
        setExperimentDetails,
      }}
    >
      {children}
    </ExperimentContext.Provider>
  );
}

export function useExperiment(): ExperimentContextType {
  return useContext(ExperimentContext) as ExperimentContextType;
}
